export type GameModeType = 'freeRoam' | 'wave' | 'timeTrial' | 'mission';

export interface ModeSelectCallback {
  (mode: GameModeType): void;
}

export class ModeSelectMenu {
  private container: HTMLElement;
  private onSelect: ModeSelectCallback | null = null;

  constructor() {
    this.container = this.createMenu();
    document.getElementById('ui-container')?.appendChild(this.container);
    this.hide();
  }

  private createMenu(): HTMLElement {
    const container = document.createElement('div');
    container.id = 'mode-select-menu';
    container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.95);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 20px;
      z-index: 300;
      font-family: 'Courier New', monospace;
      pointer-events: auto;
    `;

    container.innerHTML = `
      <div style="color: #00ffff; font-size: 64px; font-weight: bold; text-shadow: 0 0 30px #00ffff; letter-spacing: 10px; margin-bottom: 40px;">
        CYBER BIKES
      </div>

      <div style="color: #888888; font-size: 16px; margin-bottom: 20px; letter-spacing: 2px;">
        SELECT GAME MODE
      </div>

      <div id="mode-buttons" style="display: flex; flex-direction: column; gap: 15px;">
      </div>

      <div style="color: #666666; font-size: 14px; margin-top: 40px; letter-spacing: 1px;">
        CONTROLS: WASD to move | Q/E to aim up/down | LEFT CLICK to fire | R to reload
      </div>
      <div style="color: #666666; font-size: 14px; letter-spacing: 1px;">
        V to change camera | M for mode menu | ESC to pause
      </div>
    `;

    const buttonContainer = container.querySelector('#mode-buttons') as HTMLElement;

    const modes = [
      { id: 'freeRoam', name: 'FREE ROAM', desc: 'Explore the city freely', color: '#00ff88' },
      { id: 'wave', name: 'WAVE SURVIVAL', desc: 'Survive endless enemy waves', color: '#ff8800' },
      { id: 'timeTrial', name: 'TIME TRIAL', desc: 'Race against the clock', color: '#ffff00' },
      { id: 'mission', name: 'MISSIONS', desc: 'Complete tactical objectives', color: '#ff00ff' },
    ];

    for (const mode of modes) {
      const button = document.createElement('button');
      button.className = 'mode-button';
      button.dataset.mode = mode.id;
      button.style.cssText = `
        background: rgba(${this.hexToRgb(mode.color)}, 0.1);
        border: 2px solid ${mode.color};
        color: ${mode.color};
        padding: 20px 60px;
        font-size: 24px;
        font-weight: bold;
        letter-spacing: 3px;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: 'Courier New', monospace;
        min-width: 400px;
        text-align: left;
      `;

      button.innerHTML = `
        <div>${mode.name}</div>
        <div style="font-size: 12px; color: #888888; margin-top: 5px; font-weight: normal;">${mode.desc}</div>
      `;

      button.addEventListener('mouseenter', () => {
        button.style.background = `rgba(${this.hexToRgb(mode.color)}, 0.3)`;
        button.style.transform = 'scale(1.02)';
        button.style.boxShadow = `0 0 20px ${mode.color}`;
      });

      button.addEventListener('mouseleave', () => {
        button.style.background = `rgba(${this.hexToRgb(mode.color)}, 0.1)`;
        button.style.transform = 'scale(1)';
        button.style.boxShadow = 'none';
      });

      button.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectMode(mode.id as GameModeType);
      });

      buttonContainer.appendChild(button);
    }

    // Stop clicks from propagating to prevent pointer lock
    container.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    return container;
  }

  private hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '255, 255, 255';
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
  }

  private selectMode(mode: GameModeType): void {
    if (this.onSelect) {
      this.onSelect(mode);
    }
    this.hide();
  }

  public show(callback: ModeSelectCallback): void {
    this.onSelect = callback;
    this.container.style.display = 'flex';
    // Exit pointer lock so user can click menu
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
  }

  public hide(): void {
    this.container.style.display = 'none';
    this.onSelect = null;
  }

  public isVisible(): boolean {
    return this.container.style.display !== 'none';
  }

  public destroy(): void {
    this.container.remove();
  }
}
